import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { isAdminDomain, isCustomerDomain } from "@/utils/domainUtils";
import DirectAdminDashboard from "@/pages/DirectAdminDashboard";
import { CustomerLayout } from "@/components/layouts/CustomerLayout";
import { AdminLayout } from "@/components/AdminLayout";

// Route Guards
import ProtectedUserRoute from "@/components/ProtectedUserRoute";
import ProtectedAdminRoute from "@/components/ProtectedAdminRoute";
import ProtectedSuperAdminRoute from "@/components/ProtectedSuperAdminRoute";
import { AdminPageSkeleton } from "@/components/AdminPageSkeleton";

// Customer Page Skeleton for lazy loading
const CustomerPageSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-primary/10 to-secondary/10 p-4 animate-pulse">
    <div className="h-8 w-32 bg-muted rounded mb-4" />
    <div className="h-48 bg-muted rounded-xl mb-4" />
    <div className="space-y-3">
      <div className="h-12 bg-muted rounded-lg" />
      <div className="h-12 bg-muted rounded-lg" />
      <div className="h-12 bg-muted rounded-lg" />
    </div>
  </div>
);

// ============================================
// CUSTOMER PAGES - ALL LAZY LOADED
// ============================================
const Landing = lazy(() => import("@/pages/Landing"));
const InstallApp = lazy(() => import("@/pages/InstallApp"));
const Auth = lazy(() => import("@/pages/Auth"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Profile = lazy(() => import("@/pages/Profile"));
const Services = lazy(() => import("@/pages/Services"));
const TransactionHistory = lazy(() => import("@/pages/TransactionHistory"));
const P2PTransfer = lazy(() => import("@/pages/P2PTransfer"));
const P2PTransferHistoryWrapper = lazy(() => import("@/pages/P2PTransferHistoryWrapper"));
const Deposit = lazy(() => import("@/pages/Deposit"));
const Withdraw = lazy(() => import("@/pages/Withdraw"));
const ReceiveMoney = lazy(() => import("@/pages/ReceiveMoney"));
const MyQRCode = lazy(() => import("@/pages/MyQRCode"));
const Settings = lazy(() => import("@/pages/Settings"));
const Support = lazy(() => import("@/pages/Support"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const QRPayment = lazy(() => import("@/pages/QRPayment"));
const WalletManagement = lazy(() => import("@/pages/WalletManagement"));
const WalletAvatars = lazy(() => import("@/pages/WalletAvatars"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
const SecuritySettings = lazy(() => import("@/pages/SecuritySettings"));
const KYC = lazy(() => import("@/pages/KYC"));
const FixedDepositPage = lazy(() => import("@/pages/FixedDepositPage"));
const CardManagement = lazy(() => import("@/pages/CardManagement"));
const BonusCenter = lazy(() => import("@/pages/BonusCenter"));
const ReceiptView = lazy(() => import("@/pages/ReceiptView").then(m => ({ default: m.ReceiptView })));
const Analytics = lazy(() => import("@/pages/Analytics"));
const MobileCurrencyExchange = lazy(() => import("@/pages/MobileCurrencyExchange"));
const MobileTopUp = lazy(() => import("@/pages/MobileTopUp"));
const MobileMultiWallet = lazy(() => import("@/pages/MobileMultiWallet"));
const MobilePromotions = lazy(() => import("@/pages/MobilePromotions"));
const MobileBackgroundSettings = lazy(() => import("@/pages/MobileBackgroundSettings"));
const BuyAccounts = lazy(() => import("@/pages/BuyAccounts"));
const ThemePage = lazy(() => import("@/pages/ThemePage"));
const TrustedDevices = lazy(() => import("@/pages/TrustedDevices"));
const VIPLevels = lazy(() => import("@/pages/VIPLevels"));
const ReceiptDetail = lazy(() => import("@/pages/ReceiptDetail"));
const ReceiptHistory = lazy(() => import("@/pages/ReceiptHistory"));
const ReceiptVerification = lazy(() => import("@/pages/ReceiptVerification"));
const BillPayment = lazy(() => import("@/pages/BillPayment"));
const AccountStatement = lazy(() => import("@/pages/AccountStatement"));
const CreditAdjustment = lazy(() => import("@/pages/CreditAdjustment"));
const DepositWithdraw = lazy(() => import("@/pages/DepositWithdraw"));
const HelpSupport = lazy(() => import("@/pages/HelpSupport"));
const KYCReview = lazy(() => import("@/pages/KYCReview"));
const WalletDebug = lazy(() => import("@/pages/WalletDebug"));
const MyWallets = lazy(() => import("@/pages/MyWallets"));
const CurrencyTransferPage = lazy(() => import("@/pages/CurrencyTransferPage"));
const SystemDiagnosticsPage = lazy(() => import("@/pages/SystemDiagnostics"));
const MerchantPayment = lazy(() => import("@/pages/MerchantPayment"));
const MerchantDashboard = lazy(() => import("@/pages/MerchantDashboard"));
const PaymentCallback = lazy(() => import("@/pages/PaymentCallback"));
const MerchantAPIDocumentation = lazy(() => import("@/pages/MerchantAPIDocumentation"));
const MerchantIntegrationGuide = lazy(() => import("@/pages/MerchantIntegrationGuide"));
const MerchantWebhookLogs = lazy(() => import("@/pages/MerchantWebhookLogs"));
const DemoMerchantTest = lazy(() => import("@/pages/DemoMerchantTest"));
const MerchantPaymentHistory = lazy(() => import("@/pages/MerchantPaymentHistory"));
const PaymentPage = lazy(() => import("@/pages/PaymentPage"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const DepositWithdrawHistory = lazy(() => import("@/pages/DepositWithdrawHistory"));
const ChatRoom = lazy(() => import("@/pages/ChatRoom"));
const ChatInbox = lazy(() => import("@/pages/ChatInbox"));
const EnterPin = lazy(() => import("@/pages/EnterPin"));

// ============================================
// ADMIN PAGES - ALL LAZY LOADED
// ============================================
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminHome = lazy(() => import("@/pages/AdminHome"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const AdminUsersManagement = lazy(() => import("@/pages/AdminUsersManagement"));
const AdminUserCreditList = lazy(() => import("@/pages/AdminUserCreditList"));
const AdminTransactions = lazy(() => import("@/pages/AdminTransactions"));
const AdminTransactionManagement = lazy(() => import("@/pages/AdminTransactionManagement"));
const AdminTransactionLimits = lazy(() => import("@/pages/AdminTransactionLimits"));
const AdminDeposits = lazy(() => import("@/pages/AdminDeposits"));
const AdminWithdraws = lazy(() => import("@/pages/AdminWithdraws"));
const AdminFixedDeposits = lazy(() => import("@/pages/AdminFixedDeposits"));
const AdminInterestSettings = lazy(() => import("@/pages/AdminInterestSettings"));
const AdminInterestPayments = lazy(() => import("@/pages/AdminInterestPayments"));
const AdminBanks = lazy(() => import("@/pages/AdminBanks"));
const AdminSystemBanks = lazy(() => import("@/pages/AdminSystemBanks"));
const AdminCurrencies = lazy(() => import("@/pages/AdminCurrencies"));
const AdminExchangeRates = lazy(() => import("@/pages/AdminExchangeRates"));
const AdminAutoExchangeRates = lazy(() => import("@/pages/AdminAutoExchangeRates"));
const AdminFeeManagement = lazy(() => import("@/pages/AdminFeeManagement"));
const AdminReports = lazy(() => import("@/pages/AdminReports"));
const AdminReconciliation = lazy(() => import("@/pages/AdminReconciliation"));
const AdminSecurityCenter = lazy(() => import("@/pages/AdminSecurityCenter"));
const BudgetingTools = lazy(() => import("@/pages/BudgetingTools"));
const SocialFeed = lazy(() => import("@/pages/SocialFeed"));
const SupportTickets = lazy(() => import("@/pages/SupportTickets"));
const BackupManagement = lazy(() => import("@/pages/BackupManagement"));
const NotificationCenter = lazy(() => import("@/pages/NotificationCenter"));
const AdvancedReporting = lazy(() => import("@/pages/AdvancedReporting"));
const AdminNotifications = lazy(() => import("@/pages/AdminNotifications"));
const AdminRiskReport = lazy(() => import("@/pages/AdminRiskReport"));
const AdminAlertAnalytics = lazy(() => import("@/pages/AdminAlertAnalytics"));
const AdminLogs = lazy(() => import("@/pages/AdminLogs"));
const AdminReceipts = lazy(() => import("@/pages/AdminReceipts"));
const AdminPromotions = lazy(() => import("@/pages/AdminPromotions"));
const AdminLuckyWheel = lazy(() => import("@/pages/AdminLuckyWheel"));
const AdminVIPLevels = lazy(() => import("@/pages/AdminVIPLevels"));
const AdminWhatsApp = lazy(() => import("@/pages/AdminWhatsApp"));
const AdminReferralSystem = lazy(() => import("@/pages/AdminReferralSystem"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));
const AdminSupport = lazy(() => import("@/pages/AdminSupport"));
const AdminManagement = lazy(() => import("@/pages/AdminManagement"));
const AdminCardManagement = lazy(() => import("@/pages/AdminCardManagement"));
const AdminAccountInventory = lazy(() => import("@/pages/AdminAccountInventory"));
const AdminFraudDetection = lazy(() => import("@/pages/AdminFraudDetection"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const SystemMonitoring = lazy(() => import("@/pages/SystemMonitoring"));
const AdminCustomMessagesPage = lazy(() => import("@/pages/AdminCustomMessages"));
const AdminBanners = lazy(() => import("@/pages/AdminBanners"));
const AdminCompanySettings = lazy(() => import("@/pages/AdminCompanySettings"));
const AdminMerchants = lazy(() => import("@/pages/AdminMerchants"));
const AdminLandingPageSettings = lazy(() => import("@/pages/AdminLandingPageSettings"));
const AdminPaymentGateways = lazy(() => import("@/pages/AdminPaymentGateways"));
const AdminPaymentGatewayDetails = lazy(() => import("@/pages/AdminPaymentGatewayDetails"));
const AdminPaymentGatewayDashboard = lazy(() => import("@/pages/AdminPaymentGatewayDashboard"));
const AdminPaymentGatewaySettings = lazy(() => import("@/pages/AdminPaymentGatewaySettings"));
const AdminPaymentSessions = lazy(() => import("@/pages/AdminPaymentSessions"));
const AdminPaymentSessionDetails = lazy(() => import("@/pages/AdminPaymentSessionDetails"));
const AdminMerchantPaymentReports = lazy(() => import("@/pages/AdminMerchantPaymentReports"));
const AdminWebhookLogs = lazy(() => import("@/pages/AdminWebhookLogs"));
const AdminTestMerchant = lazy(() => import("@/pages/AdminTestMerchant"));
const AdminAuthPerformance = lazy(() => import("@/pages/AdminAuthPerformance"));
const AdminKYCReview = lazy(() => import("@/pages/AdminKYCReview"));
const PaymentGatewayAPIDocs = lazy(() => import("@/pages/PaymentGatewayAPIDocs"));

function CustomerRoutes() {
  return (
    <Routes>
      {/* Landing Page at root - with Suspense */}
      <Route path="/" element={
        <Suspense fallback={<CustomerPageSkeleton />}>
          <Landing />
        </Suspense>
      } />
      
      {/* Auth page at /auth without CustomerLayout but with providers access */}
      <Route path="/auth" element={
        <Suspense fallback={<CustomerPageSkeleton />}>
          <Auth />
        </Suspense>
      } />
      
      {/* PWA Install page - no layout needed */}
      <Route path="/install" element={
        <Suspense fallback={<CustomerPageSkeleton />}>
          <InstallApp />
        </Suspense>
      } />
      
      {/* Enter PIN page - fullscreen lock screen */}
      <Route path="/enter-pin" element={
        <Suspense fallback={<CustomerPageSkeleton />}>
          <EnterPin />
        </Suspense>
      } />
      
      {/* All other routes with CustomerLayout */}
      <Route path="/*" element={
        <CustomerLayout>
          <Suspense fallback={<CustomerPageSkeleton />}>
            <Routes>
              <Route path="/mobile-banking" element={<Navigate to="/auth" replace />} />
              
              {/* Protected Customer Routes */}
              <Route path="/dashboard" element={
                <ProtectedUserRoute>
                  <Dashboard />
                </ProtectedUserRoute>
              } />
              <Route path="/profile" element={
                <ProtectedUserRoute>
                  <Profile />
                </ProtectedUserRoute>
              } />
              <Route path="/services" element={
                <ProtectedUserRoute>
                  <Services />
                </ProtectedUserRoute>
              } />
              <Route path="/transactions" element={
                <ProtectedUserRoute>
                  <TransactionHistory />
                </ProtectedUserRoute>
              } />
              <Route path="/transaction-history" element={
                <ProtectedUserRoute>
                  <TransactionHistory />
                </ProtectedUserRoute>
              } />
              <Route path="/transfer" element={
                <ProtectedUserRoute>
                  <P2PTransfer />
                </ProtectedUserRoute>
              } />
              <Route path="/p2p-transfer" element={
                <ProtectedUserRoute>
                  <P2PTransfer />
                </ProtectedUserRoute>
              } />
              <Route path="/p2p-transfer-history" element={
                <ProtectedUserRoute>
                  <P2PTransferHistoryWrapper />
                </ProtectedUserRoute>
              } />
              <Route path="/deposit" element={
                <ProtectedUserRoute>
                  <Deposit />
                </ProtectedUserRoute>
              } />
              <Route path="/withdraw" element={
                <ProtectedUserRoute>
                  <Withdraw />
                </ProtectedUserRoute>
              } />
              <Route path="/receive-money" element={
                <ProtectedUserRoute>
                  <ReceiveMoney />
                </ProtectedUserRoute>
              } />
              <Route path="/my-qr-code" element={
                <ProtectedUserRoute>
                  <MyQRCode />
                </ProtectedUserRoute>
              } />
              <Route path="/deposit-withdraw-history" element={
                <ProtectedUserRoute>
                  <DepositWithdrawHistory />
                </ProtectedUserRoute>
              } />
              <Route path="/chat-inbox" element={
                <ProtectedUserRoute>
                  <ChatInbox />
                </ProtectedUserRoute>
              } />
              <Route path="/chat/:conversationId" element={
                <ProtectedUserRoute>
                  <ChatRoom />
                </ProtectedUserRoute>
              } />
              <Route path="/qr-payment" element={
                <ProtectedUserRoute>
                  <QRPayment />
                </ProtectedUserRoute>
              } />
              <Route path="/wallet-management" element={
                <ProtectedUserRoute>
                  <WalletManagement />
                </ProtectedUserRoute>
              } />
              <Route path="/wallet-debug" element={
                <ProtectedUserRoute>
                  <WalletDebug />
                </ProtectedUserRoute>
              } />
              <Route path="/wallet-avatars" element={
                <ProtectedUserRoute>
                  <WalletAvatars />
                </ProtectedUserRoute>
              } />
              <Route path="/my-wallets" element={
                <ProtectedUserRoute>
                  <MyWallets />
                </ProtectedUserRoute>
              } />
              <Route path="/currency-transfer" element={
                <ProtectedUserRoute>
                  <CurrencyTransferPage />
                </ProtectedUserRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedUserRoute>
                  <Notifications />
                </ProtectedUserRoute>
              } />
              <Route path="/help-center" element={
                <ProtectedUserRoute>
                  <HelpCenter />
                </ProtectedUserRoute>
              } />
              <Route path="/security-settings" element={
                <ProtectedUserRoute>
                  <SecuritySettings />
                </ProtectedUserRoute>
              } />
              <Route path="/kyc" element={
                <ProtectedUserRoute>
                  <KYC />
                </ProtectedUserRoute>
              } />
              <Route path="/fixed-deposit" element={
                <ProtectedUserRoute>
                  <FixedDepositPage />
                </ProtectedUserRoute>
              } />
              <Route path="/card-management" element={
                <ProtectedUserRoute>
                  <CardManagement />
                </ProtectedUserRoute>
              } />
              <Route path="/bonus-center" element={
                <ProtectedUserRoute>
                  <BonusCenter />
                </ProtectedUserRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedUserRoute>
                  <Analytics />
                </ProtectedUserRoute>
              } />
              <Route path="/mobile-currency-exchange" element={
                <ProtectedUserRoute>
                  <MobileCurrencyExchange />
                </ProtectedUserRoute>
              } />
              <Route path="/mobile-top-up" element={
                <ProtectedUserRoute>
                  <MobileTopUp />
                </ProtectedUserRoute>
              } />
              <Route path="/mobile-multi-wallet" element={
                <ProtectedUserRoute>
                  <MobileMultiWallet />
                </ProtectedUserRoute>
              } />
              <Route path="/mobile-promotions" element={
                <ProtectedUserRoute>
                  <MobilePromotions />
                </ProtectedUserRoute>
              } />
              <Route path="/mobile-background-settings" element={
                <ProtectedUserRoute>
                  <MobileBackgroundSettings />
                </ProtectedUserRoute>
              } />
              <Route path="/buy-accounts" element={
                <ProtectedUserRoute>
                  <BuyAccounts />
                </ProtectedUserRoute>
              } />
              <Route path="/theme" element={
                <ProtectedUserRoute>
                  <ThemePage />
                </ProtectedUserRoute>
              } />
              <Route path="/trusted-devices" element={
                <ProtectedUserRoute>
                  <TrustedDevices />
                </ProtectedUserRoute>
              } />
              <Route path="/vip-levels" element={
                <ProtectedUserRoute>
                  <VIPLevels />
                </ProtectedUserRoute>
              } />
              <Route path="/receipt/:transactionId" element={
                <ProtectedUserRoute>
                  <ReceiptDetail />
                </ProtectedUserRoute>
              } />
              <Route path="/receipt-history" element={
                <ProtectedUserRoute>
                  <ReceiptHistory />
                </ProtectedUserRoute>
              } />
              <Route path="/receipt-verification/:transactionId" element={
                <ProtectedUserRoute>
                  <ReceiptVerification />
                </ProtectedUserRoute>
              } />
              <Route path="/bill-payment" element={
                <ProtectedUserRoute>
                  <BillPayment />
                </ProtectedUserRoute>
              } />
              <Route path="/account-statement" element={
                <ProtectedUserRoute>
                  <AccountStatement />
                </ProtectedUserRoute>
              } />
              <Route path="/settings" element={
                <ProtectedUserRoute>
                  <Settings />
                </ProtectedUserRoute>
              } />
              <Route path="/support" element={
                <ProtectedUserRoute>
                  <Support />
                </ProtectedUserRoute>
              } />
              <Route path="/receipt" element={
                <ProtectedUserRoute>
                  <ReceiptView />
                </ProtectedUserRoute>
              } />
              <Route path="/system-diagnostics" element={
                <ProtectedUserRoute>
                  <SystemDiagnosticsPage />
                </ProtectedUserRoute>
              } />
              
              {/* Merchant Routes */}
              <Route path="/merchant/payment/:sessionId" element={<MerchantPayment />} />
              <Route path="/merchant/dashboard" element={
                <ProtectedUserRoute>
                  <MerchantDashboard />
                </ProtectedUserRoute>
              } />
              <Route path="/merchant/api-docs" element={
                <ProtectedUserRoute>
                  <MerchantAPIDocumentation />
                </ProtectedUserRoute>
              } />
              <Route path="/merchant/integration-guide" element={
                <ProtectedUserRoute>
                  <MerchantIntegrationGuide />
                </ProtectedUserRoute>
              } />
              <Route path="/merchant/webhook-logs" element={
                <ProtectedUserRoute>
                  <MerchantWebhookLogs />
                </ProtectedUserRoute>
              } />
              <Route path="/merchant/demo-test" element={
                <ProtectedUserRoute>
                  <DemoMerchantTest />
                </ProtectedUserRoute>
              } />
              <Route path="/merchant/payment-history" element={
                <ProtectedUserRoute>
                  <MerchantPaymentHistory />
                </ProtectedUserRoute>
              } />
              
              {/* Payment Routes - Require Login */}
              <Route path="/pay/:sessionId" element={
                <ProtectedUserRoute>
                  <PaymentPage />
                </ProtectedUserRoute>
              } />
              <Route path="/payment/success" element={
                <ProtectedUserRoute>
                  <PaymentSuccess />
                </ProtectedUserRoute>
              } />
              <Route path="/payment/callback" element={
                <ProtectedUserRoute>
                  <PaymentCallback />
                </ProtectedUserRoute>
              } />
              
              {/* Demo/Test Routes - Public access for API testing */}
              <Route path="/demo/merchant-test" element={<DemoMerchantTest />} />
              
              {/* Admin Routes accessible from customer domain */}
              <Route path="/credit-adjustment" element={
                <ProtectedUserRoute>
                  <CreditAdjustment />
                </ProtectedUserRoute>
              } />
              <Route path="/deposit-withdraw" element={
                <ProtectedUserRoute>
                  <DepositWithdraw />
                </ProtectedUserRoute>
              } />
              <Route path="/help-support" element={
                <ProtectedUserRoute>
                  <HelpSupport />
                </ProtectedUserRoute>
              } />
              <Route path="/kyc-review" element={
                <ProtectedUserRoute>
                  <KYCReview />
                </ProtectedUserRoute>
              } />
              
              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </CustomerLayout>
      } />
    </Routes>
  );
}

function AdminRoutes() {
  console.log('🔑 AdminRoutes: Rendering admin routes');
  console.log('🔑 AdminRoutes: Current path:', window.location.pathname);
  return (
    <Routes>
      {/* Admin home page - requires login to access dashboard */}
      <Route path="/" element={<AdminHome />} />
      <Route path="/login" element={<AdminLogin />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      
      {/* ============================================ */}
      {/* REDIRECT ROUTES - เมนูซ้ำที่ redirect ไปหน้าหลัก */}
      {/* ============================================ */}
      <Route path="/admin/users" element={<Navigate to="/admin/users-management" replace />} />
      <Route path="/admin/transactions" element={<Navigate to="/admin/transaction-management" replace />} />
      <Route path="/admin/fee-settings" element={<Navigate to="/admin/fee-management" replace />} />
      <Route path="/admin/dashboard-fixed" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/full-dashboard" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/logs-realtime" element={<Navigate to="/admin/logs" replace />} />
      <Route path="/admin/logs-filter" element={<Navigate to="/admin/logs" replace />} />
      <Route path="/admin/alerts-analytics" element={<Navigate to="/admin/alert-analytics" replace />} />
      
      {/* ============================================ */}
      {/* PROTECTED ADMIN ROUTES - ทุก route มี Protection + Layout */}
      {/* ============================================ */}
      
      {/* Dashboard */}
      <Route path="/dashboard" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <DirectAdminDashboard />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/dashboard" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <DirectAdminDashboard />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      
      {/* Dashboard & Overview */}
      <Route path="/admin/system-monitoring" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <SystemMonitoring />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/alert-analytics" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminAlertAnalytics />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/risk-report" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminRiskReport />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/fraud-detection" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminFraudDetection />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/security-center" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminSecurityCenter />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      
      {/* User Management */}
      <Route path="/admin/users-management" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminUsersManagement />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/users-credit" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminUserCreditList />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/admin-management" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminManagement />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/kyc-review" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminKYCReview />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      
      {/* Financial Management */}
      <Route path="/admin/transaction-management" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminTransactionManagement />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/deposits" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminDeposits />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/withdraws" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminWithdraws />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/reconciliation" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminReconciliation />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/fee-management" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminFeeManagement />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/transaction-limits" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminTransactionLimits />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      
      {/* Interest & Deposits */}
      <Route path="/admin/interest-settings" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminInterestSettings />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/interest-payments" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminInterestPayments />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/fixed-deposits" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminFixedDeposits />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      
      {/* Banks & Currency */}
      <Route path="/admin/banks" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminBanks />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/system-banks" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminSystemBanks />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/currencies" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminCurrencies />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/exchange-rates" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminExchangeRates />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/auto-exchange-rates" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminAutoExchangeRates />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      
      {/* Payment Gateway */}
      <Route path="/admin/payment-gateway-dashboard" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminPaymentGatewayDashboard />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/payment-gateways" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminPaymentGateways />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/payment-gateways/:id" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminPaymentGatewayDetails />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/payment-sessions" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminPaymentSessions />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/payment-sessions/:sessionId" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminPaymentSessionDetails />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/merchant-payment-reports" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminMerchantPaymentReports />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/webhook-logs" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminWebhookLogs />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/merchants" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminMerchants />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/test-merchant" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminTestMerchant />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/payment-gateway-settings" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminPaymentGatewaySettings />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/payment-gateway-api-docs" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <PaymentGatewayAPIDocs />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      
      {/* Promotions & Events */}
      <Route path="/admin/promotions" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminPromotions />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/lucky-wheel" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminLuckyWheel />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/banners" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminBanners />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/vip-levels" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminVIPLevels />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/referral-system" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminReferralSystem />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      
      {/* Reports & System */}
      <Route path="/admin/reports" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminReports />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/advanced-reporting" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdvancedReporting />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/notifications" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminNotifications />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/backup-management" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <BackupManagement />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/logs" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminLogs />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/receipts" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminReceipts />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      
      {/* Support & Settings */}
      <Route path="/admin/support" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminSupport />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/whatsapp" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminWhatsApp />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/company-settings" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminCompanySettings />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/custom-messages" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminCustomMessagesPage />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/auth-performance" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminAuthPerformance />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/card-management" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminCardManagement />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/account-inventory" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminAccountInventory />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/panel" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminPanel />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminSettings />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/landing-page-settings" element={
        <ProtectedAdminRoute>
          <AdminLayout>
            <Suspense fallback={<AdminPageSkeleton />}>
              <AdminLandingPageSettings />
            </Suspense>
          </AdminLayout>
        </ProtectedAdminRoute>
      } />
      
      {/* Customer-related routes accessible from admin */}
      <Route path="/budgeting" element={<ProtectedUserRoute><Suspense fallback={<AdminPageSkeleton />}><BudgetingTools /></Suspense></ProtectedUserRoute>} />
      <Route path="/social-feed" element={<ProtectedUserRoute><Suspense fallback={<AdminPageSkeleton />}><SocialFeed /></Suspense></ProtectedUserRoute>} />
      <Route path="/support-tickets" element={<ProtectedUserRoute><Suspense fallback={<AdminPageSkeleton />}><SupportTickets /></Suspense></ProtectedUserRoute>} />
      <Route path="/notification-center" element={<ProtectedUserRoute><Suspense fallback={<AdminPageSkeleton />}><NotificationCenter /></Suspense></ProtectedUserRoute>} />
      
      <Route path="*" element={<Navigate to="/admin/login" replace />} />
    </Routes>
  );
}

export default function DomainRouter() {
  console.log('🚀 DomainRouter: Starting domain routing');
  console.log('🚀 DomainRouter: Current pathname:', window.location.pathname);
  
  // Force admin routes for /admin paths - HIGHEST PRIORITY
  if (window.location.pathname.startsWith('/admin')) {
    console.log('🚀 DomainRouter: Force rendering AdminRoutes for /admin path');
    return <AdminRoutes />;
  }
  
  const isAdmin = isAdminDomain();
  const isCustomer = isCustomerDomain();
  
  console.log('🚀 DomainRouter:', { isAdmin, isCustomer });
  
  // Route based on domain
  if (isAdmin) {
    console.log('🚀 DomainRouter: Rendering AdminRoutes');
    return <AdminRoutes />;
  }
  
  console.log('🚀 DomainRouter: Rendering CustomerRoutes (default)');
  return <CustomerRoutes />;
}
